import { IotaClient } from "@iota/iota-sdk/client";
import { Transaction } from "@iota/iota-sdk/transactions";
import { fromBase64 } from "@iota/bcs";

const GAS_BUDGET = 100_000_000;

export function buildCreateTwinTransaction(context, input) {
  const tx = new Transaction();
  const authority = context.subscriptionId
    ? [tx.object(context.subscriptionId), tx.object(context.controllerCapId)]
    : [tx.object(input.creditTokenId), tx.object(context.creditPolicyId), tx.object(context.controllerCapId)];
  tx.moveCall({
    target: `${context.executionPackageId || context.packageId}::oid_twin::create_twin`,
    arguments: [
      ...authority,
      tx.pure.string(input.twinType), tx.pure.string(input.targetKind), tx.pure.option("address", input.targetObjectId || null),
      tx.pure.string(input.targetDid || ""), tx.pure.u8(Number(input.lifecycleState)), tx.pure.u8(Number(input.fidelityLevel)),
      tx.pure.u8(Number(input.maturityLevel)), tx.pure.string(input.name), tx.pure.string(input.description || ""),
      tx.pure.string(input.namespace), tx.pure.string(input.immutableMetadata || "{}"), tx.pure.string(input.mutableMetadata || "{}"),
      tx.object(context.clockId),
    ],
  });
  tx.setGasBudget(GAS_BUDGET);
  return tx;
}

export function buildDeleteTwinTransaction(context, twinId, creditTokenId) {
  const tx = new Transaction();
  tx.moveCall({
    target: `${context.executionPackageId || context.packageId}::oid_twin::delete_twin`,
    arguments: context.subscriptionId
      ? [tx.object(context.subscriptionId), tx.object(context.controllerCapId), tx.object(twinId), tx.object(context.clockId)]
      : [tx.object(creditTokenId), tx.object(context.creditPolicyId), tx.object(context.controllerCapId), tx.object(twinId), tx.object(context.clockId)],
  });
  tx.setGasBudget(GAS_BUDGET);
  return tx;
}

export function buildStewardMigrationTransaction(context, twinId, integrationControllerId, integrationDid) {
  if (!context.subscriptionId) throw new Error("A subscription account is required");
  if (!context.ownerControllerId) throw new Error("The subscription owner controller is required");
  const tx = new Transaction();
  const subscription = tx.object(context.subscriptionId);
  const ownerCap = tx.object(context.controllerCapId);
  const clock = tx.object(context.clockId);
  const assignmentTarget = `${context.executionPackageId || context.packageId}::oid_twin::assign_subscription_integration`;

  // Legacy Twins created directly by the Webview named the customer as steward. Return authority
  // to the owner only inside this PTB, assign the DTIS as steward, and restore DTIS control.
  tx.moveCall({ target: assignmentTarget, arguments: [
    subscription, ownerCap, tx.pure.address(context.ownerControllerId), clock,
  ] });
  tx.moveCall({ target: `${context.executionPackageId || context.packageId}::oid_twin::transfer_twin_steward`, arguments: [
    subscription, ownerCap, tx.object(twinId), tx.pure.string(integrationDid), clock,
  ] });
  tx.moveCall({ target: assignmentTarget, arguments: [
    subscription, ownerCap, tx.pure.address(integrationControllerId), clock,
  ] });
  tx.setGasBudget(GAS_BUDGET);
  return tx;
}

export function buildAssignIntegrationTransaction(context, integrationControllerId) {
  if (!context.subscriptionId) throw new Error("A subscription account is required");
  const tx = new Transaction();
  tx.moveCall({
    target: `${context.executionPackageId || context.packageId}::oid_twin::assign_subscription_integration`,
    arguments: [
      tx.object(context.subscriptionId), tx.object(context.controllerCapId),
      tx.pure.address(integrationControllerId), tx.object(context.clockId),
    ],
  });
  tx.setGasBudget(GAS_BUDGET);
  return tx;
}

export async function executeTwinTransaction({ keypair, network, transaction }) {
  const client = new IotaClient({ url: network === "mainnet" ? "https://api.mainnet.iota.cafe" : "https://api.testnet.iota.cafe" });
  transaction.setSender(keypair.toIotaAddress());
  const submitted = await client.signAndExecuteTransaction({ signer: keypair, transaction });
  const result = await client.waitForTransaction({ digest: submitted.digest, options: { showEffects: true, showObjectChanges: true } });
  const status = result.effects?.status?.status;
  if (status && status !== "success") throw new Error(result.effects?.status?.error || "IOTA transaction failed");
  return result;
}

export async function executeSponsoredCreateTwin({ keypair, input }) {
  const preparedResponse = await fetch("/api/my/twins/create/prepare", {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(input),
  });
  const prepared = await preparedResponse.json();
  if (!preparedResponse.ok) throw new Error(prepared.error || "Unable to prepare sponsored transaction");
  const signed = await keypair.signTransaction(fromBase64(prepared.transactionBytes));
  const executeResponse = await fetch("/api/my/twins/create/execute", {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ pendingId: prepared.pendingId, signature: signed.signature }),
  });
  const result = await executeResponse.json();
  if (!executeResponse.ok) throw new Error(result.error || "Unable to execute sponsored transaction");
  return result;
}

export async function executeSponsoredDeleteTwin({ keypair, twinId, creditTokenId }) {
  const preparedResponse = await fetch("/api/my/twins/delete/prepare", {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ twinId, creditTokenId }),
  });
  const prepared = await preparedResponse.json();
  if (!preparedResponse.ok) throw new Error(prepared.error || "Unable to prepare sponsored deletion");
  if (prepared.stewardReady) return deleteThroughIntegration(twinId);
  const signed = await keypair.signTransaction(fromBase64(prepared.transactionBytes));
  const executeResponse = await fetch("/api/my/twins/delete/execute", {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ pendingId: prepared.pendingId, signature: signed.signature }),
  });
  const result = await executeResponse.json();
  if (!executeResponse.ok) throw new Error(result.error || "Unable to execute sponsored deletion");
  if (result.migrated) {
    const deleted = await deleteThroughIntegration(twinId);
    return { ...deleted, stewardMigrationDigest: result.digest };
  }
  return result;
}

async function deleteThroughIntegration(twinId) {
  const response = await fetch(`/api/my/twins/${encodeURIComponent(twinId)}`, {
    method: "DELETE", headers: { "Content-Type": "application/json", "X-ObjectID-Confirm-Delete": twinId },
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result?.error?.message ?? result?.error ?? "Unable to delete the Digital Twin");
  return result;
}

export async function executeSponsoredIntegrationAssignment({ keypair }) {
  const preparedResponse = await fetch("/api/my/integration-assignment/prepare", { method: "POST" });
  const prepared = await preparedResponse.json();
  if (!preparedResponse.ok) throw new Error(prepared.error || "Unable to prepare integration assignment");
  if (prepared.alreadyAssigned) return prepared;
  const signed = await keypair.signTransaction(fromBase64(prepared.transactionBytes));
  const executeResponse = await fetch("/api/my/integration-assignment/execute", {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ pendingId: prepared.pendingId, signature: signed.signature }),
  });
  const result = await executeResponse.json();
  if (!executeResponse.ok) throw new Error(result.error || "Unable to assign the integration server");
  return result;
}

export function createdTwinId(result, packageId) {
  return result.objectChanges?.find((change) => change.type === "created" && change.objectType === `${packageId}::oid_twin::OIDTwin`)?.objectId ?? "";
}

export function usableCreditTokens(context) {
  if (context?.subscriptionId) return [{ objectId: context.subscriptionId, balance: String(context.remainingCredits ?? "1") }];
  return (context?.creditTokens ?? []).filter((token) => /^\d+$/.test(token.balance) && BigInt(token.balance) > 0n);
}
